import { useEffect, useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { formatApiError } from '@/lib/api';
import api from '@/lib/api';
import { SupportHelpLine, useSupportPhone, telHref } from '@/components/SupportHelpLine';
import { QuickStartDialog, useQuickStartAutoOpen } from '@/components/QuickStartGuide';
import {
  ArrowLeft, EnvelopeSimple, Key, CheckCircle,
  Users, Receipt, ChartLineUp, UsersFour, ChartBar, CloudArrowUp,
  Kanban, CurrencyInr, FolderOpen, WhatsappLogo,
  ArrowRight, Star, TrendUp, X,
  Buildings, Briefcase, ShieldStar, FileText, Scales, Airplane, UsersThree, Calculator, Storefront,
  Target, Lightbulb,
} from '@phosphor-icons/react';

// ────────────────────────────────────────────────────────────────────
//  Reference-inspired landing (SMSUITEPRO style).
//  - Light hero with dashboard mockup on right
//  - Auto-scrolling industry marquee
//  - 6-card "Everything your centre needs" grid
//  - "Powerful features" alt list
//  - 6-persona "Built for" grid
//  - 4-step "How it works" flow
//  - Dynamic pricing (2 plans preview + link to full pricing)
//  - Testimonials
//  - Final CTA
//  Login form is exposed via a "Sign in" modal so the sticky header
//  stays clean and matches the reference visual language.
// ────────────────────────────────────────────────────────────────────

const INDUSTRY_MARQUEE = [
  'CSC CENTRES', 'AKSHAYA', 'JANASEVA KENDRAS', 'INSURANCE AGENTS',
  'TAX CONSULTANTS', 'DIGITAL SERVICE CENTRES', 'SMALL BUSINESSES',
];

const NEEDS = [
  { icon: Users,        title: 'Customer Management', desc: 'One organised database for every client and lead.' },
  { icon: Receipt,      title: 'Billing & Invoice',   desc: 'Professional GST-ready invoices in seconds.' },
  { icon: CurrencyInr,  title: 'Finance Management',  desc: 'Track income, expenses, cash & bank books.' },
  { icon: UsersFour,    title: 'Staff Management',    desc: 'Accounts, attendance, salary & permissions.' },
  { icon: ChartBar,     title: 'Reports & Analytics', desc: 'Daily and monthly insights that drive growth.' },
  { icon: CloudArrowUp, title: 'Cloud Backup',        desc: 'Your data secured and synced automatically.' },
];

const POWER_FEATURES = [
  { icon: Users,        title: 'CRM / Customer Management', desc: 'Store, search and filter your entire customer base with full service history and documents.' },
  { icon: Kanban,       title: 'Service Management',        desc: 'Track every service request from intake to delivery with status pipelines.' },
  { icon: Receipt,      title: 'Billing & Invoice',         desc: 'Create invoices, record payments, print receipts and manage credit.' },
  { icon: CurrencyInr,  title: 'Income & Expense Tracking', desc: 'Know your daily cash position with cash book, bank book and profit reports.' },
  { icon: UsersFour,    title: 'Staff Management',          desc: 'Add staff, mark attendance, track salaries and set role-based permissions.' },
  { icon: FolderOpen,   title: 'Document Management',       desc: 'Upload and organise customer documents securely in the cloud.' },
  { icon: WhatsappLogo, title: 'WhatsApp Reminder',         desc: 'Send automated payment and service reminders on WhatsApp.' },
  { icon: ChartLineUp,  title: 'Reports',                   desc: 'Actionable daily, monthly and business analytics dashboards.' },
];

const PERSONAS = [
  { icon: Calculator, title: 'CA Firms',                 desc: 'Compliance & filings'   },
  { icon: FileText,   title: 'Tax Consultants',          desc: 'GST, ITR, advisory'     },
  { icon: ShieldStar, title: 'Insurance Agencies',       desc: 'Policies & renewals'    },
  { icon: Buildings,  title: 'CSC / Akshaya / Janaseva', desc: 'Govt service centres'   },
  { icon: Scales,     title: 'Legal Offices',            desc: 'Matter tracking'        },
  { icon: Airplane,   title: 'Travel Agencies',          desc: 'Bookings & vouchers'    },
  { icon: UsersThree, title: 'HR & Recruitment',         desc: 'Candidate pipelines'    },
  { icon: Briefcase,  title: 'Accounting Services',      desc: 'Books & receivables'    },
  { icon: Storefront, title: 'Any Service Business',     desc: 'Built to fit yours'     },
];

const HIGHLIGHTS = [
  { icon: TrendUp,   label: 'Grow billings'    },
  { icon: Target,    label: 'Track every case' },
  { icon: Lightbulb, label: 'Stay compliant'   },
];

const STEPS = [
  { n: '01', title: 'Create Account',              desc: 'Sign up in under two minutes — no credit card required.' },
  { n: '02', title: 'Add Business Details',        desc: 'Set up your centre profile, services and staff.' },
  { n: '03', title: 'Manage Customers & Services', desc: 'Start billing, tracking and serving customers.' },
  { n: '04', title: 'Track Business Growth',       desc: 'Watch reports and analytics reveal your progress.' },
];

const TESTIMONIALS = [
  { name: 'Rajesh Kumar',  role: 'CSC Centre Owner, Patna',       initials: 'RK', quote: 'SMSuitePro replaced three registers and two apps. My billing is faster and customers get instant WhatsApp reminders.' },
  { name: 'Anitha Menon',  role: 'Akshaya Entrepreneur, Kochi',   initials: 'AM', quote: 'The reports show me exactly how much I earn each day. Setup took ten minutes and my staff picked it up instantly.' },
  { name: 'Suresh Patel',  role: 'Insurance Agent, Ahmedabad',    initials: 'SP', quote: 'Renewal reminders alone paid for the subscription. It\u2019s the most affordable software I\u2019ve used for my agency.' },
  { name: 'Priya Sharma',  role: 'Tax Consultant, Jaipur',        initials: 'PS', quote: 'All my client documents and invoices live in one secure place. The cloud backup gives me real peace of mind.' },
];

// Brand mark — the SMSuitePro logo image (chart-glyph + wordmark + tagline).
// Sized presets keep it consistent across nav bar / footer / dialogs.
export function BrandMark({ size = 'md', asLink = true }) {
  const h = { sm: 'h-9', md: 'h-[58px]', lg: 'h-20' }[size] || 'h-[58px]';
  const img = (
    <img
      src="https://customer-assets-4nw71qhi.emergentagent.net/job_service-mgmt-system/artifacts/4yp1zn97_smsuitepro-logo.jpg"
      alt="SMSuitePro — Service Management Suite Pro"
      className={`${h} w-auto object-contain select-none`}
      draggable={false}
    />
  );
  if (asLink) {
    return (
      <Link to="/" className="inline-flex items-center leading-none" data-testid="brand-mark">
        {img}
      </Link>
    );
  }
  return (
    <div className="inline-flex items-center leading-none" data-testid="brand-mark">{img}</div>
  );
}

// Shared marketing header — used on every public page so the menu looks identical.
export function MarketingHeader({ onSignInClick }) {
  return (
    <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-[#E4E4E7]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between gap-4">
        <BrandMark size="md" />
        <nav className="hidden lg:flex items-center gap-6 text-sm font-semibold text-[#3F3F46]">
          <Link to="/"         className="hover:text-[#1E3A8A]" data-testid="nav-home">Home</Link>
          <Link to="/features" className="hover:text-[#1E3A8A]" data-testid="nav-features">Features</Link>
          <Link to="/pricing"  className="hover:text-[#1E3A8A]" data-testid="nav-pricing">Pricing</Link>
          <Link to="/about"    className="hover:text-[#1E3A8A]" data-testid="nav-about">About</Link>
          <Link to="/quick-start-guide" className="hover:text-[#1E3A8A]" data-testid="nav-quick-start">Quick Start Guide</Link>
          <Link to="/faq"      className="hover:text-[#1E3A8A]" data-testid="nav-faq">FAQ</Link>
          <Link to="/contact"  className="hover:text-[#1E3A8A]" data-testid="nav-contact">Contact</Link>
        </nav>
        <div className="flex items-center gap-2 sm:gap-3">
          {onSignInClick ? (
            <button
              type="button"
              onClick={onSignInClick}
              className="text-sm font-semibold text-[#0A0A0A] px-2 py-1 hover:underline"
              data-testid="nav-signin"
            >Login</button>
          ) : (
            <Link to="/login" className="text-sm font-semibold text-[#0A0A0A] px-2 py-1 hover:underline" data-testid="nav-signin">Login</Link>
          )}
          <Link
            to="/signup"
            className="inline-flex items-center gap-1 rounded-lg bg-[#F97316] text-white text-sm font-semibold px-4 py-2 shadow-sm shadow-orange-500/20 hover:bg-[#EA580C]"
            data-testid="nav-trial"
          >Start Free Trial</Link>
        </div>
      </div>
    </header>
  );
}

// Shared marketing footer — used on every public page.
export function MarketingFooter() {
  const supportPhone = useSupportPhone();
  return (
    <footer className="border-t border-[#E4E4E7] bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-2 md:grid-cols-4 gap-8">
        <div className="col-span-2 md:col-span-1">
          <BrandMark size="sm" />
          <p className="text-xs text-[#71717A] mt-4 leading-relaxed">
            All-in-one business management platform for service centres.
          </p>
          <div className="mt-4 space-y-1.5 text-xs">
            <a
              href="mailto:smsuitepro@gmail.com"
              className="flex items-center gap-1.5 text-[#52525B] hover:text-[#1E3A8A]"
              data-testid="footer-email"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
              smsuitepro@gmail.com
            </a>
            <a
              href={telHref(supportPhone)}
              className="flex items-center gap-1.5 text-[#52525B] hover:text-[#1E3A8A]"
              data-testid="footer-phone"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
              {supportPhone}
            </a>
          </div>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-[0.15em] font-bold text-[#0A0A0A] mb-3">Product</div>
          <ul className="space-y-2 text-sm text-[#52525B]">
            <li><Link to="/features" className="hover:text-[#1E3A8A]">Features</Link></li>
            <li><Link to="/pricing"  className="hover:text-[#1E3A8A]">Pricing</Link></li>
            <li><Link to="/demo-request" className="hover:text-[#1E3A8A]">Request Demo</Link></li>
            <li><Link to="/signup" className="hover:text-[#1E3A8A]">Start Free Trial</Link></li>
          </ul>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-[0.15em] font-bold text-[#0A0A0A] mb-3">Company</div>
          <ul className="space-y-2 text-sm text-[#52525B]">
            <li><Link to="/about"   className="hover:text-[#1E3A8A]">About</Link></li>
            <li><Link to="/contact" className="hover:text-[#1E3A8A]">Contact</Link></li>
            <li><Link to="/faq"     className="hover:text-[#1E3A8A]">FAQ</Link></li>
            <li><Link to="/quick-start-guide" className="hover:text-[#1E3A8A]" data-testid="footer-quick-start">Quick Start Guide</Link></li>
          </ul>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-[0.15em] font-bold text-[#0A0A0A] mb-3">Legal</div>
          <ul className="space-y-2 text-sm text-[#52525B]">
            <li><Link to="/privacy" className="hover:text-[#1E3A8A]">Privacy</Link></li>
            <li><Link to="/terms"   className="hover:text-[#1E3A8A]">Terms</Link></li>
            <li><Link to="/refund-policy" className="hover:text-[#1E3A8A]">Refund</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-[#E4E4E7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 text-xs text-[#71717A] text-center">
          (c) {new Date().getFullYear()} SMSuitePro. All rights reserved.
        </div>
      </div>
    </footer>
  );
}

// ────────────────────────────────────────────────────────────────────
//  Sign-in modal — the marketing header opens this so visitors can log
//  in without leaving the landing page.
// ────────────────────────────────────────────────────────────────────
function SignInModal({ open, onClose, next }) {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState('login');
  const savedEmail = localStorage.getItem('remembered_login_id') || '';
  const [email, setEmail] = useState(savedEmail);
  const [rememberMe, setRememberMe] = useState(!!savedEmail);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  if (!open) return null;

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const u = await login(email, password);
      if (rememberMe) localStorage.setItem('remembered_login_id', email);
      else localStorage.removeItem('remembered_login_id');
      const safeNext = next && next.startsWith('/') && !next.startsWith('//') ? next : null;
      navigate(u?.role === 'super_admin' ? '/superadmin' : (safeNext || '/dashboard'));
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email) { setError('Enter your email address'); return; }
    setError(''); setLoading(true);
    try {
      const { data } = await api.post('/auth/forgot-password', { email });
      if (data.reset_token) setResetToken(data.reset_token);
      setMode('reset');
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!newPassword) { setError('Enter a new password'); return; }
    if (newPassword.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (newPassword !== confirmPassword) { setError('Passwords do not match'); return; }
    setError(''); setLoading(true);
    try {
      await api.post('/auth/reset-password', { token: resetToken, new_password: newPassword });
      setMode('done');
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#0A0A0A]/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="signin-modal">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl border border-[#E4E4E7] p-8 relative">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 text-[#71717A] hover:text-[#0A0A0A]"
          data-testid="signin-modal-close"
        ><X size={20} /></button>

        <div className="mb-6"><BrandMark size="sm" asLink={false} /></div>

        {mode === 'login' && (
          <>
            <h2 className="text-2xl font-bold tracking-tight mb-1" style={{ fontFamily: 'var(--font-heading)' }}>Welcome back</h2>
            <p className="text-sm text-[#52525B] mb-6">Sign in to your SMSuitePro account</p>
            {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg" data-testid="login-error">{error}</div>}
            <form onSubmit={handleLogin} className="space-y-4" autoComplete="off">
              <div className="space-y-2">
                <Label className="text-xs tracking-wide uppercase font-semibold text-[#52525B]">Email</Label>
                <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" required className="rounded-lg border-[#E4E4E7]" data-testid="login-email-input" autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck="false" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs tracking-wide uppercase font-semibold text-[#52525B]">Password</Label>
                <Input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Your password" required className="rounded-lg border-[#E4E4E7]" data-testid="login-password-input" autoComplete="new-password" autoCorrect="off" autoCapitalize="off" spellCheck="false" />
              </div>
              <label className="flex items-center gap-2 text-sm text-[#52525B] cursor-pointer select-none" data-testid="remember-me-label">
                <input type="checkbox" checked={rememberMe} onChange={e => setRememberMe(e.target.checked)} className="h-4 w-4 rounded border-[#D4D4D8] accent-[#1E3A8A]" data-testid="remember-me-checkbox" />
                Remember my login ID on this browser
              </label>
              <Button type="submit" disabled={loading} className="w-full bg-[#1E3A8A] text-white hover:bg-[#1E40AF] hover:text-white rounded-lg h-10" data-testid="login-submit-btn">
                {loading ? 'Signing in...' : 'Sign in'}
              </Button>
            </form>
            <div className="mt-4 flex items-center justify-between text-sm">
              <button type="button" onClick={() => { setMode('forgot'); setError(''); }} className="text-[#71717A] hover:text-[#0A0A0A]" data-testid="forgot-password-link">Forgot password?</button>
              <Link to="/signup" className="font-semibold text-[#F97316] hover:text-[#EA580C]" data-testid="signup-link">Start Free Trial →</Link>
            </div>
            <SupportHelpLine className="mt-5 pt-4 border-t border-[#F4F4F5]" testid="login-support-help" />
          </>
        )}

        {mode === 'forgot' && (
          <>
            <button onClick={() => { setMode('login'); setError(''); }} className="flex items-center gap-1 text-sm text-[#52525B] hover:text-[#0A0A0A] mb-4" data-testid="back-to-login">
              <ArrowLeft size={14} /> Back to sign in
            </button>
            <div className="w-12 h-12 bg-[#EFF6FF] rounded-xl flex items-center justify-center mb-4">
              <EnvelopeSimple size={24} className="text-[#1E3A8A]" />
            </div>
            <h2 className="text-2xl tracking-tight font-bold mb-1" style={{ fontFamily: 'var(--font-heading)' }}>Forgot password?</h2>
            <p className="text-sm text-[#52525B] mb-6">Enter your email to receive a password reset token</p>
            {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg" data-testid="forgot-error">{error}</div>}
            <form onSubmit={handleForgotPassword} className="space-y-5" autoComplete="off">
              <div className="space-y-2">
                <Label className="text-xs tracking-wide uppercase font-semibold text-[#52525B]">Email address</Label>
                <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" required className="rounded-lg border-[#E4E4E7]" data-testid="forgot-email-input" autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck="false" />
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-[#1E3A8A] text-white hover:bg-[#1E40AF] hover:text-white rounded-lg h-10" data-testid="forgot-submit-btn">
                {loading ? 'Sending...' : 'Send Reset Token'}
              </Button>
            </form>
          </>
        )}

        {mode === 'reset' && (
          <>
            <div className="w-12 h-12 bg-[#EFF6FF] rounded-xl flex items-center justify-center mb-4">
              <Key size={24} className="text-[#1E3A8A]" />
            </div>
            <h2 className="text-2xl tracking-tight font-bold mb-1" style={{ fontFamily: 'var(--font-heading)' }}>Reset password</h2>
            <p className="text-sm text-[#52525B] mb-6">Enter the reset token and your new password</p>
            {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg" data-testid="reset-error">{error}</div>}
            <form onSubmit={handleResetPassword} className="space-y-4" autoComplete="off">
              <div className="space-y-2">
                <Label className="text-xs tracking-wide uppercase font-semibold text-[#52525B]">Reset Token</Label>
                <Input value={resetToken} onChange={e => setResetToken(e.target.value)} placeholder="Paste token here" required className="rounded-lg border-[#E4E4E7] font-mono text-sm" data-testid="reset-token-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs tracking-wide uppercase font-semibold text-[#52525B]">New Password</Label>
                <Input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Min 6 characters" required className="rounded-lg border-[#E4E4E7]" data-testid="new-password-input" autoComplete="new-password" autoCorrect="off" autoCapitalize="off" spellCheck="false" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs tracking-wide uppercase font-semibold text-[#52525B]">Confirm Password</Label>
                <Input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Re-enter password" required className="rounded-lg border-[#E4E4E7]" data-testid="confirm-password-input" autoComplete="new-password" autoCorrect="off" autoCapitalize="off" spellCheck="false" />
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-[#1E3A8A] text-white hover:bg-[#1E40AF] hover:text-white rounded-lg h-10" data-testid="reset-submit-btn">
                {loading ? 'Resetting...' : 'Reset Password'}
              </Button>
            </form>
            <button onClick={() => { setMode('login'); setError(''); }} className="mt-4 text-sm text-[#52525B] hover:text-[#0A0A0A] block w-full text-center" data-testid="back-to-login-from-reset">
              Back to sign in
            </button>
          </>
        )}

        {mode === 'done' && (
          <div className="text-center">
            <div className="w-16 h-16 bg-[#D1FAE5] rounded-xl flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={32} className="text-[#10B981]" weight="fill" />
            </div>
            <h2 className="text-2xl tracking-tight font-bold mb-2" style={{ fontFamily: 'var(--font-heading)' }}>Password Reset!</h2>
            <p className="text-sm text-[#52525B] mb-6">Your password has been successfully reset. You can now sign in with your new password.</p>
            <Button onClick={() => { setMode('login'); setError(''); setPassword(''); }} className="w-full bg-[#1E3A8A] text-white hover:bg-[#1E40AF] hover:text-white rounded-lg h-10" data-testid="go-to-login-btn">
              Back to Sign In
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
//  Landing page
// ────────────────────────────────────────────────────────────────────
export default function LoginPage() {
  const [trialDays, setTrialDays] = useState(15);
  const [plans, setPlans] = useState([]);
  const [searchParams] = useSearchParams();
  const next = searchParams.get('next');
  const [signInOpen, setSignInOpen] = useState(!!next);
  // Landing pricing toggle — 'month' | 'year'.
  const [billingCycle, setBillingCycle] = useState('month');
  const [guideOpen, setGuideOpen] = useQuickStartAutoOpen('visitor', !next && !signInOpen, 4000);

  useEffect(() => {
    let cancelled = false;
    api.get('/marketing/config')
      .then(r => {
        if (cancelled) return;
        setTrialDays(r.data?.trial_days ?? 15);
        setPlans(r.data?.plans || []);
      })
      .catch(() => { /* fall back to defaults */ });
    return () => { cancelled = true; };
  }, []);

  // No preview slice — landing shows every plan, matching the /pricing table.
  const previewPlans = plans;

  return (
    <div className="min-h-screen bg-white text-[#0A0A0A]" data-testid="login-page">
      <MarketingHeader onSignInClick={() => setSignInOpen(true)} />
      <SignInModal open={signInOpen} onClose={() => setSignInOpen(false)} next={next} />
      <QuickStartDialog open={guideOpen} onOpenChange={setGuideOpen} seenKey="visitor" />

      {/* ─────────────────── HERO ─────────────────── */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#F8FAFC] via-white to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#EFF6FF] border border-[#DBEAFE] text-[11px] uppercase tracking-[0.18em] font-bold text-[#1E3A8A] mb-6" data-testid="hero-tagline-pill">
              Manage. Track. Grow.
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] mb-5" style={{ fontFamily: 'var(--font-heading)' }}>
              The all-in-one suite
              <span className="block">for <span style={{ color: '#F97316' }}>service-led</span> businesses.</span>
            </h1>
            <p className="text-base sm:text-lg text-[#52525B] leading-relaxed max-w-2xl mb-6">
              <span style={{ color: '#1E3A8A' }} className="font-semibold">SMSuite</span><span style={{ color: '#F97316' }} className="font-semibold">Pro</span> brings invoicing, customer ledgers, follow-ups
              and finance into one calm, keyboard-first workspace — so every
              minute at the counter compounds into growth.
            </p>

            {/* Manage · Track · Grow highlight chips */}
            <div className="flex flex-wrap gap-2 mb-8">
              {HIGHLIGHTS.map(({ icon: Icon, label }) => (
                <div
                  key={label}
                  data-testid={`hero-highlight-${label.toLowerCase().replace(/\s+/g, '-')}`}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-[#E4E4E7] text-sm shadow-sm"
                >
                  <Icon size={14} weight="duotone" className="text-[#1E3A8A]" />
                  <span className="text-[#0A0A0A] font-medium">{label}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-3 mb-8">
              <Link
                to="/signup"
                className="inline-flex items-center gap-2 bg-[#F97316] text-white hover:bg-[#EA580C] rounded-xl px-5 py-3 text-sm font-semibold shadow-lg shadow-orange-500/20 transition-colors"
                data-testid="hero-cta-trial"
              >
                Start Free Trial <ArrowRight size={16} weight="bold" />
              </Link>
              <Link
                to="/demo-request"
                className="inline-flex items-center gap-2 rounded-xl border border-[#E4E4E7] px-5 py-3 text-sm font-semibold text-[#0A0A0A] hover:bg-[#F4F4F5]"
                data-testid="hero-cta-demo"
              >Request Demo</Link>
              <button
                type="button"
                onClick={() => setSignInOpen(true)}
                className="inline-flex items-center gap-2 rounded-xl px-5 py-3 text-sm font-semibold text-[#1E3A8A] hover:text-[#1E40AF] underline underline-offset-4"
                data-testid="hero-cta-login"
              >Login</button>
            </div>

            <div className="flex items-center gap-4">
              <div className="flex -space-x-2">
                {['#1E3A8A', '#F97316', '#10B981', '#8B5CF6'].map(c => (
                  <div key={c} className="w-8 h-8 rounded-full border-2 border-white" style={{ background: c }} />
                ))}
              </div>
              <div className="text-sm text-[#52525B]">
                <b className="text-[#0A0A0A]">2,500+</b> service centres trust us
              </div>
            </div>
          </div>

          {/* Dashboard mockup on the right */}
          <div className="lg:col-span-5 relative">
            <div className="relative rounded-2xl overflow-hidden border border-[#E4E4E7] shadow-2xl shadow-blue-900/10" data-testid="hero-visual">
              <img
                src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=900&q=80"
                alt="SMSuitePro dashboard preview"
                className="w-full h-[360px] object-cover"
              />
            </div>

            <div className="absolute -top-6 -left-6 hidden sm:block bg-white rounded-2xl shadow-xl shadow-blue-900/10 p-4 w-56 border border-[#E4E4E7]" data-testid="hero-stat-revenue">
              <div className="text-[10px] uppercase tracking-[0.15em] text-[#71717A] font-semibold mb-1">Today&apos;s Revenue</div>
              <div className="text-2xl font-bold tracking-tight text-[#0A0A0A]">₹1,28,400</div>
              <div className="text-[11px] text-[#10B981] font-semibold mt-1 flex items-center gap-1">
                <TrendUp size={11} weight="bold" /> +18% this week
              </div>
            </div>

            <div className="absolute -bottom-6 -right-4 hidden sm:flex items-center gap-3 bg-white rounded-2xl shadow-xl shadow-blue-900/10 p-3 pr-4 border border-[#E4E4E7]" data-testid="hero-stat-invoices">
              <div className="w-10 h-10 rounded-xl bg-[#EFF6FF] flex items-center justify-center">
                <Receipt size={20} weight="duotone" className="text-[#1E3A8A]" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.12em] text-[#71717A] font-semibold">Invoices</div>
                <div className="text-sm font-bold text-[#0A0A0A]">37 · today</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────────────── INDUSTRY MARQUEE ─────────────────── */}
      <section className="bg-[#0F172A] text-white border-y border-[#0F172A] overflow-hidden" data-testid="industry-marquee">
        <div className="relative py-5">
          <div className="flex gap-12 animate-[marquee_35s_linear_infinite] whitespace-nowrap will-change-transform">
            {[...INDUSTRY_MARQUEE, ...INDUSTRY_MARQUEE].map((label, i) => (
              <div key={i} className="flex items-center gap-12 text-sm sm:text-base font-bold tracking-[0.25em] text-white/80">
                {label}
                <span className="w-2 h-2 rounded-full bg-[#F97316]" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────────────── EVERYTHING YOUR CENTRE NEEDS ─────────────────── */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
          <div className="text-center mb-12">
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#F97316] font-bold mb-3">Trusted business solution</div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#0A0A0A] mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
              Everything your centre needs, in one place
            </h2>
            <p className="text-base text-[#52525B] max-w-2xl mx-auto">
              Replace scattered registers, spreadsheets and apps with a single, reliable platform.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {NEEDS.map(({ icon: Icon, title, desc }) => (
              <div
                key={title}
                data-testid={`need-${title.toLowerCase().replace(/\s+/g, '-')}`}
                className="rounded-2xl border border-[#E4E4E7] bg-white p-6 hover:border-[#1E3A8A] hover:shadow-lg hover:shadow-blue-900/5 transition-all duration-300"
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#EFF6FF] to-[#DBEAFE] flex items-center justify-center mb-4">
                  <Icon size={24} weight="duotone" className="text-[#1E3A8A]" />
                </div>
                <div className="text-lg font-bold tracking-tight text-[#0A0A0A] mb-1" style={{ fontFamily: 'var(--font-heading)' }}>{title}</div>
                <div className="text-sm text-[#52525B] leading-relaxed">{desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────────────── POWERFUL FEATURES ─────────────────── */}
      <section className="bg-gradient-to-b from-[#F8FAFC] to-white border-y border-[#E4E4E7]" id="features">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
          <div className="text-center mb-12">
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#F97316] font-bold mb-3">Features overview</div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#0A0A0A] mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
              Powerful features, beautifully simple
            </h2>
            <Link to="/features" className="text-sm font-semibold text-[#1E3A8A] hover:text-[#1E40AF] inline-flex items-center gap-1" data-testid="explore-all-features">
              Explore all features <ArrowRight size={14} weight="bold" />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {POWER_FEATURES.map(({ icon: Icon, title, desc }) => (
              <div
                key={title}
                data-testid={`feature-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                className="rounded-2xl bg-white p-5 border border-[#E4E4E7] hover:border-[#1E3A8A]/40 hover:shadow-lg hover:shadow-blue-900/5 transition-all"
              >
                <div className="w-10 h-10 rounded-xl bg-[#EFF6FF] flex items-center justify-center mb-3">
                  <Icon size={20} weight="duotone" className="text-[#1E3A8A]" />
                </div>
                <div className="text-sm font-bold text-[#0A0A0A] tracking-tight mb-1">{title}</div>
                <div className="text-xs text-[#52525B] leading-snug">{desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────────────── WHO CAN USE IT ─────────────────── */}
      <section className="bg-white" id="personas">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
          <div className="text-center mb-12">
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#F97316] font-bold mb-3">Suitable for</div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#0A0A0A] mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
              Built for service-led businesses of every kind
            </h2>
            <p className="text-base text-[#52525B] max-w-2xl mx-auto">
              Whether you run a tax practice, an insurance desk or a busy CSC counter — the same fundamentals power your day.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {PERSONAS.map(({ icon: Icon, title, desc }, i) => (
              <div
                key={title}
                data-testid={`persona-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                className="group relative rounded-2xl border border-[#E4E4E7] bg-white p-5 hover:border-[#1E3A8A] hover:shadow-lg hover:shadow-blue-900/5 transition-all duration-300 overflow-hidden"
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-[#EFF6FF] to-transparent rounded-full -translate-y-12 translate-x-12 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="relative">
                  <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#EFF6FF] to-[#DBEAFE] flex items-center justify-center mb-4 group-hover:from-[#1E3A8A] group-hover:to-[#3B82F6] transition-colors">
                    <Icon size={22} weight="duotone" className="text-[#1E3A8A] group-hover:text-white transition-colors" />
                  </div>
                  <div className="text-base font-semibold tracking-tight text-[#0A0A0A] mb-1">{title}</div>
                  <div className="text-xs text-[#71717A]">{desc}</div>
                  <div className="absolute top-1 right-1 text-[10px] font-mono text-[#A1A1AA]">{String(i + 1).padStart(2, '0')}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────────────── HOW IT WORKS ─────────────────── */}
      <section className="bg-[#0F172A] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
          <div className="text-center mb-12">
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#F97316] font-bold mb-3">How it works</div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
              From sign-up to growth in four steps
            </h2>
            <p className="text-base text-white/70 max-w-2xl mx-auto">
              Getting started with SMSuitePro takes minutes, not weeks.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 relative">
            {STEPS.map(({ n, title, desc }) => (
              <div key={n} data-testid={`step-${n}`} className="relative rounded-2xl bg-white/5 border border-white/10 p-6 backdrop-blur-sm">
                <div className="text-3xl font-extrabold text-[#F97316] mb-3" style={{ fontFamily: 'var(--font-heading)' }}>{n}</div>
                <div className="text-lg font-bold mb-2" style={{ fontFamily: 'var(--font-heading)' }}>{title}</div>
                <div className="text-sm text-white/70 leading-relaxed">{desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────────────── PRICING (preview) ─────────────────── */}
      <section className="bg-white" id="pricing">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
          <div className="text-center mb-8">
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#F97316] font-bold mb-3">Simple pricing</div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#0A0A0A] mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
              Plans that grow with your business
            </h2>
            <p className="text-base text-[#52525B] max-w-2xl mx-auto">
              Start free for {trialDays} days, then choose a plan that fits. No hidden fees.
            </p>
          </div>

          {/* Monthly / Yearly toggle — swaps the display price. Plans with an
              explicit `yearly_price` show a "Save %" chip; the rest fall
              back to monthly × 12 without a chip. */}
          <div className="flex justify-center mb-10">
            <div className="inline-flex items-center gap-1 bg-[#F4F4F5] rounded-full p-1 text-sm font-semibold" data-testid="pricing-billing-toggle">
              <button
                type="button"
                onClick={() => setBillingCycle('month')}
                className={`px-5 py-2 rounded-full transition-colors ${billingCycle === 'month' ? 'bg-white text-[#0A0A0A] shadow-sm' : 'text-[#71717A] hover:text-[#0A0A0A]'}`}
                data-testid="pricing-toggle-monthly"
              >Monthly</button>
              <button
                type="button"
                onClick={() => setBillingCycle('year')}
                className={`px-5 py-2 rounded-full transition-colors flex items-center gap-2 ${billingCycle === 'year' ? 'bg-white text-[#0A0A0A] shadow-sm' : 'text-[#71717A] hover:text-[#0A0A0A]'}`}
                data-testid="pricing-toggle-yearly"
              >
                Yearly
                {(() => {
                  const best = plans.reduce((m, p) => {
                    if (!p.yearly_price || !p.price) return m;
                    const pct = Math.round(100 - (p.yearly_price / (p.price * 12)) * 100);
                    return Math.max(m, pct);
                  }, 0);
                  return best > 0 ? (
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#10B981] text-white">Save {best}%</span>
                  ) : null;
                })()}
              </button>
            </div>
          </div>

          <div className={`grid grid-cols-1 sm:grid-cols-2 ${previewPlans.length >= 3 ? 'lg:grid-cols-3' : ''} gap-6 max-w-5xl mx-auto`}>
            {previewPlans.map((p, i) => {
              const isHighlight = previewPlans.length >= 2 && i === 1;
              const monthlyEq = p.price * 12;
              const yearlyPrice = p.yearly_price || monthlyEq;
              const savePct = p.yearly_price && p.price ? Math.round(100 - (p.yearly_price / monthlyEq) * 100) : 0;
              const displayPrice = billingCycle === 'year' ? yearlyPrice : p.price;
              const displayPeriod = billingCycle === 'year' ? 'year' : (p.period || 'month');
              const displayMrp = billingCycle === 'year' && p.mrp ? p.mrp * 12 : p.mrp;
              return (
                <div
                  key={p.id}
                  data-testid={`pricing-card-${p.id}`}
                  className={`relative rounded-2xl border p-6 flex flex-col transition-all duration-300 ${isHighlight ? 'border-[#1E3A8A] shadow-xl shadow-blue-900/10 bg-gradient-to-b from-white to-[#EFF6FF]/40 scale-[1.02]' : 'border-[#E4E4E7] bg-white hover:border-[#1E3A8A]/50 hover:shadow-lg'}`}
                >
                  {isHighlight && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-[#F97316] text-white text-[10px] uppercase tracking-wider font-bold shadow-lg shadow-orange-500/20">
                      Most Popular
                    </div>
                  )}
                  <div className="text-[11px] uppercase tracking-[0.15em] text-[#71717A] font-bold mb-1">Plan</div>
                  <div className="text-xl font-bold text-[#0A0A0A] mb-3" style={{ fontFamily: 'var(--font-heading)' }}>{p.name}</div>
                  <div className="flex items-baseline gap-2 mb-1">
                    <span className="text-4xl font-extrabold text-[#0A0A0A]" style={{ fontFamily: 'var(--font-heading)' }} data-testid={`pricing-price-${p.id}`}>₹{Number(displayPrice || 0).toLocaleString('en-IN')}</span>
                    <span className="text-sm text-[#71717A]">/{displayPeriod}</span>
                  </div>
                  {billingCycle === 'year' && savePct > 0 ? (
                    <div className="text-xs mb-4">
                      <span className="text-[#71717A] line-through">₹{monthlyEq.toLocaleString('en-IN')}</span>
                      <span className="ml-2 text-[#059669] font-semibold">Save {savePct}% · billed annually</span>
                    </div>
                  ) : displayMrp && displayMrp !== displayPrice ? (
                    <div className="text-xs text-[#71717A] mb-4">
                      <span className="line-through">MRP ₹{displayMrp.toLocaleString('en-IN')}</span>
                      <span className="ml-2 text-[#059669] font-semibold">Save {Math.round(100 - (displayPrice / displayMrp) * 100)}%</span>
                    </div>
                  ) : <div className="mb-4" />}
                  <ul className="space-y-2 mb-6 flex-1">
                    {(p.features || []).map((f, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-[#0A0A0A]">
                        <CheckCircle size={16} weight="fill" className="text-[#10B981] mt-0.5 shrink-0" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/signup"
                    className={`inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-semibold transition-colors ${isHighlight ? 'bg-[#F97316] text-white hover:bg-[#EA580C]' : 'bg-[#0A0A0A] text-white hover:bg-[#27272A]'}`}
                    data-testid={`pricing-cta-${p.id}`}
                  >
                    Start Free Trial <ArrowRight size={14} weight="bold" />
                  </Link>
                </div>
              );
            })}
            {previewPlans.length === 0 && (
              <div className="col-span-full text-center text-sm text-[#A1A1AA] py-8">Loading plans…</div>
            )}
          </div>

          <div className="text-center mt-10">
            <Link to="/pricing" className="inline-flex items-center gap-1 text-sm font-semibold text-[#1E3A8A] hover:text-[#1E40AF]" data-testid="view-full-pricing">
              View full pricing <ArrowRight size={14} weight="bold" />
            </Link>
          </div>
        </div>
      </section>

      {/* ─────────────────── TESTIMONIALS ─────────────────── */}
      <section className="bg-gradient-to-b from-white to-[#F8FAFC] border-t border-[#E4E4E7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
          <div className="text-center mb-12">
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#F97316] font-bold mb-3">Testimonials</div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#0A0A0A]" style={{ fontFamily: 'var(--font-heading)' }}>
              Loved by service centres everywhere
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {TESTIMONIALS.map(({ name, role, initials, quote }) => (
              <div
                key={name}
                data-testid={`testimonial-${name.toLowerCase().replace(/\s+/g, '-')}`}
                className="rounded-2xl bg-white p-6 border border-[#E4E4E7] hover:shadow-lg hover:shadow-blue-900/5 transition-all flex flex-col"
              >
                <div className="flex items-center gap-1 mb-3 text-[#F97316]">
                  {[1,2,3,4,5].map(i => <Star key={i} size={14} weight="fill" />)}
                </div>
                <p className="text-sm text-[#0A0A0A] leading-relaxed mb-5 flex-1">&ldquo;{quote}&rdquo;</p>
                <div className="flex items-center gap-3 pt-3 border-t border-[#E4E4E7]">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#1E3A8A] to-[#3B82F6] text-white flex items-center justify-center text-xs font-bold">{initials}</div>
                  <div>
                    <div className="text-sm font-bold text-[#0A0A0A]">{name}</div>
                    <div className="text-xs text-[#71717A]">{role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────────────── FINAL CTA ─────────────────── */}
      <section className="bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20 text-center">
          <div className="rounded-3xl bg-gradient-to-br from-[#1E3A8A] via-[#1E40AF] to-[#0F172A] text-white p-10 sm:p-14 shadow-2xl shadow-blue-900/20">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              Ready to simplify your business?
            </h2>
            <p className="text-base sm:text-lg text-white/80 max-w-2xl mx-auto mb-8">
              Join service centres across India managing their entire operation with SMSuitePro.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <Link
                to="/signup"
                className="inline-flex items-center gap-2 bg-[#F97316] text-white hover:bg-[#EA580C] rounded-xl px-6 py-3 text-sm font-semibold shadow-lg shadow-orange-500/30 transition-colors"
                data-testid="footer-cta-trial"
              >
                Start {trialDays}-day Free Trial <ArrowRight size={16} weight="bold" />
              </Link>
              <Link
                to="/pricing"
                className="inline-flex items-center gap-2 rounded-xl border border-white/30 text-white px-6 py-3 text-sm font-semibold hover:bg-white/10"
                data-testid="footer-cta-pricing"
              >View Pricing</Link>
            </div>
          </div>
        </div>
      </section>

      <MarketingFooter />

      {/* Marquee keyframes injected once; scoped by animation name. */}
      <style>{`
        @keyframes marquee {
          0%   { transform: translateX(0);     }
          100% { transform: translateX(-50%);  }
        }
      `}</style>
    </div>
  );
}
